/**
 * Single source of truth for everything the Compare Sales dialog
 * renders. Two layers:
 *
 *  1. **Display view** (``lsView`` / ``xeroView``): credit-aware
 *     interpretation of the LS sale. Hides credit-workaround LS lines
 *     from the line-item table and surfaces them as Credit entries in
 *     Payments & credits. The totals block, the per-row gutter icons
 *     and the delta chips all consume this layer.
 *  2. **Raw-totals match logic** (``saleMismatches`` + flags below):
 *     drives the dialog-header mismatch chips and the exception-list
 *     path. Stays on raw backend totals to avoid shifting exception
 *     counts on the surrounding list view.
 *
 * Co-located with CompareSalesView so the table, totals block and
 * payments box read the same hook output - any drift between "totals
 * block thinks they match" and "payments box thinks they don't"
 * becomes impossible.
 */
import { useMemo } from 'react'
import type { CompareSalesResponse } from '../../../../services/inspector/api'
import {
  collectSaleMismatches,
  SALE_MISMATCH_KEYS,
} from '../../../../utils/saleMismatchIssues'
import { xeroPaymentsForMatch } from '../../../../utils/salesFormatters'
import { DisplayRow } from '../../../../utils/saleDisplayRows'
import { formatBusinessDate } from '../../../../utils/inspectorFormatters'
import {
  deriveLsSaleView,
  deriveSaleLines,
  deriveXeroSaleView,
  pairPaymentEntries,
  PaymentEntryPair,
  SaleCompareView,
  valuesMatch,
} from './saleCompareView'

export type { PaymentEntry, PaymentEntryPair, SaleCompareView, AmountDueState } from './saleCompareView'

export interface CompareSalesData {
  // Direct response slices - kept so renderers can read the original
  // status / kind / contact fields without re-deriving from the prop.
  lightspeed: CompareSalesResponse['lightspeed']
  xero: CompareSalesResponse['xero']
  xeroMissing: boolean

  // ----- Display view (credit-aware) -----
  /** Line-item rows rendered by the table, with credit-workaround and
   *  synthetic-note rows already filtered out. ``showDiffsOnly`` is
   *  applied on top of this. */
  saleLines: DisplayRow[]
  lsView: SaleCompareView
  xeroView: SaleCompareView

  /** Per-totals-row match flags driving the centre gutter icons. */
  subtotalExMatch: boolean
  taxMatch: boolean
  saleTotalMatch: boolean
  totalPaidMatch: boolean
  amountDueMatch: boolean

  /** Visual pairing of LS and Xero payment entries. */
  paymentPairs: PaymentEntryPair[]

  // ----- Notes row -----
  lsNote: string | null
  xeroNote: string | null
  hasAnyNote: boolean

  // ----- Sale-level mismatch flags (raw-totals, drive dialog header chips) -----
  clientMismatch: boolean
  dateMismatch: boolean
  paymentsMismatch: boolean
  updatedDateMismatch: boolean

  // ----- Layout helper -----
  /** rowSpan absorbed by the EmptySidePanel when Xero is missing. */
  survivingBodyRowSpan: number
}

/** Pull the displayable note string from a synthetic-note item. */
const extractNote = (
  data: CompareSalesResponse,
  side: 'ls' | 'xero',
): string | null => {
  const items = side === 'ls' ? data.lightspeed.items : data.xero.items
  for (const item of items) {
    if (!item.is_synthetic_note) continue
    const text =
      'product_name' in item
        ? (item as { product_name?: string }).product_name
        : (item as { description?: string }).description
    const trimmed = (text ?? '').trim()
    if (trimmed) return trimmed
  }
  return null
}

export const useCompareSalesData = (
  data: CompareSalesResponse | undefined,
  showDiffsOnly: boolean,
): CompareSalesData | null => {
  return useMemo<CompareSalesData | null>(() => {
    if (!data) return null

    const { lightspeed, xero, comparison } = data
    const xeroMissing = xero?.kind === 'missing'

    // Display rows the table actually renders. ``showDiffsOnly`` is a
    // UI toggle, not part of the abstraction, so we layer it on top.
    const allSaleLines = deriveSaleLines(data)
    const saleLines = showDiffsOnly
      ? allSaleLines.filter(r => r.isError || r.isWarning)
      : allSaleLines

    const lsNote = extractNote(data, 'ls')
    const xeroNote = extractNote(data, 'xero')
    const hasAnyNote = !!(lsNote || xeroNote)

    // Per-side credit-aware view drives the totals block + payments
    // block + delta chips + gutter icons.
    const lsView = deriveLsSaleView(data)
    const xeroView = deriveXeroSaleView(data)
    const paymentPairs = pairPaymentEntries(
      lsView.paymentEntries,
      xeroView.paymentEntries,
    )

    const subtotalExMatch = valuesMatch(lsView.subtotalEx, xeroView.subtotalEx)
    const taxMatch = valuesMatch(lsView.totalTax, xeroView.totalTax)
    const saleTotalMatch = valuesMatch(lsView.saleTotal, xeroView.saleTotal)
    const totalPaidMatch = valuesMatch(lsView.totalPaid, xeroView.totalPaid)
    const amountDueMatch = valuesMatch(lsView.amountDue, xeroView.amountDue)

    // Sale-level mismatch flags use RAW backend totals (decoupled from
    // the credit-aware display view) so the dialog header chips +
    // exception-list path stay in lock-step with the backend tagger.
    const errorCount = comparison.error_count ?? 0
    const xeroPaymentsForMatchValue = xeroPaymentsForMatch(
      lightspeed.payments_total,
      xero.amount_paid,
      xero.amount_credited,
      !!(xero.amount_credited && xero.amount_credited > 0),
      xero.kind,
      xero.total_inc_tax,
    )
    const saleMismatches = collectSaleMismatches({
      ls: {
        date: lightspeed.sale_date,
        client: lightspeed.customer_name,
        status: lightspeed.status,
        amount_inc_tax: lightspeed.total_inc_tax,
        amount_ex_tax: lightspeed.total_ex_tax,
        tax: lightspeed.total_tax,
        payments_total: lightspeed.payments_total ?? 0,
      },
      xero: {
        date: xero.invoice_date,
        client: xero.contact_name,
        status: xero.status,
        amount_inc_tax: xero.total_inc_tax,
        amount_ex_tax: xero.total_ex_tax,
        tax: xero.total_tax,
        payments_total: xeroPaymentsForMatchValue,
      },
      lineItemsMismatch: errorCount > 0,
      lsLineCount: lightspeed.count,
      xeroLineCount: xero.count,
    })
    const clientMismatch = saleMismatches.some(m => m.key === 'client')
    const dateMismatch = saleMismatches.some(m => m.key === 'sale_date')
    const paymentsMismatch = saleMismatches.some(
      m => m.key === SALE_MISMATCH_KEYS.payments,
    )

    const lsUpdatedDateLabel = lightspeed.ls_updated_at
      ? formatBusinessDate(lightspeed.ls_updated_at)
      : null
    const xeroUpdatedDateLabel = xero.xero_updated_date_utc
      ? formatBusinessDate(xero.xero_updated_date_utc)
      : null
    const updatedDateMismatch =
      !!lsUpdatedDateLabel &&
      !!xeroUpdatedDateLabel &&
      lsUpdatedDateLabel !== xeroUpdatedDateLabel

    // Body-row count the rowSpan'd EmptySidePanel cell needs to absorb:
    // every line item below the first, plus 1 for the Notes row when
    // present. The Totals row no longer lives in the table body, so it
    // does not contribute to this count.
    const survivingBodyRowSpan =
      xeroMissing && saleLines.length > 0
        ? saleLines.length + (hasAnyNote ? 1 : 0)
        : 0

    return {
      lightspeed,
      xero,
      xeroMissing,
      saleLines,
      lsView,
      xeroView,
      subtotalExMatch,
      taxMatch,
      saleTotalMatch,
      totalPaidMatch,
      amountDueMatch,
      paymentPairs,
      lsNote,
      xeroNote,
      hasAnyNote,
      clientMismatch,
      dateMismatch,
      paymentsMismatch,
      updatedDateMismatch,
      survivingBodyRowSpan,
    }
  }, [data, showDiffsOnly])
}
